import React, { useState, useEffect } from 'react';
import { Cat, Vaccination } from '../types';
import { compressImage } from '../services/db';
import { X, Plus, Trash2, Camera, Calendar, Loader2, FileText, AlertTriangle } from 'lucide-react';

interface CatFormProps {
  initialData?: Cat | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (cat: Cat) => void;
  onDelete: (id: string) => Promise<boolean>; // Return type updated to Promise<boolean>
  existingVaccinations: string[];
  existingGodparents: string[];
}

export const CatForm: React.FC<CatFormProps> = ({ initialData, isOpen, onClose, onSave, onDelete, existingVaccinations, existingGodparents }) => {
  const [formData, setFormData] = useState<Partial<Cat>>({
    name: '',
    gender: 'female',
    terrain: 1,
    chip: undefined,
    isNeutered: false,
    photos: [],
    vaccinations: [],
    admissionDate: new Date().toISOString().split('T')[0],
    notes: '',
    passportNumber: '',
  });
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false); // New local loading state

  const [newVacName, setNewVacName] = useState('');
  const [newVacDate, setNewVacDate] = useState('');

  const [nextVaccinationName, setNextVaccinationName] = useState('');
  const [nextVaccinationDate, setNextVaccinationDate] = useState('');

  const [chipSerialNumber, setChipSerialNumber] = useState('');
  const [chipLocation, setChipLocation] = useState('');

  useEffect(() => {
    if (initialData) {
      setFormData(JSON.parse(JSON.stringify(initialData)));
      setChipSerialNumber(initialData.chip?.serialNumber || '');
      setChipLocation(initialData.chip?.location || '');
      setNextVaccinationName(initialData.nextVaccination?.name || '');
      setNextVaccinationDate(initialData.nextVaccination?.date || '');
    } else {
      setFormData({
        // We do NOT set a random ID here anymore. 
        // ID is generated on save based on name + date
        id: '', 
        name: '',
        gender: 'female',
        terrain: 1,
        chip: undefined,
        isNeutered: false,
        photos: [],
        vaccinations: [],
        admissionDate: new Date().toISOString().split('T')[0],
        notes: '',
        passportNumber: '',
      });
      setChipSerialNumber('');
      setChipLocation('');
      setNextVaccinationName('');
      setNextVaccinationDate('');
    }
    // Reset inputs when modal opens/closes
    setNewVacName('');
    setNewVacDate('');
    setIsDeleting(false);
  }, [initialData, isOpen]);

  const handleChange = (field: keyof Cat, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateChip = () => {
    if (chipSerialNumber.trim() && chipLocation.trim()) {
      setFormData(prev => ({ 
        ...prev, 
        chip: { serialNumber: chipSerialNumber.trim(), location: chipLocation.trim() } 
      }));
    } else {
      setFormData(prev => ({ ...prev, chip: undefined }));
    }
  };

  const updateNextVaccination = () => {
    if (nextVaccinationName.trim() && nextVaccinationDate.trim()) {
      // Validate that next vaccination date is in the future
      if (new Date(nextVaccinationDate) <= new Date()) {
        // Don't update if date is not in the future
        return;
      }
      setFormData(prev => ({ 
        ...prev, 
        nextVaccination: { name: nextVaccinationName.trim(), date: nextVaccinationDate.trim() } 
      }));
    } else {
      setFormData(prev => ({ ...prev, nextVaccination: undefined }));
    }
  };

  useEffect(() => {
    updateChip();
  }, [chipSerialNumber, chipLocation]);

  useEffect(() => {
    updateNextVaccination();
  }, [nextVaccinationName, nextVaccinationDate]);

  const handleAddVaccination = () => {
    if (newVacName && newVacDate) {
      const newVac: Vaccination = {
        id: crypto.randomUUID(),
        name: newVacName,
        date: newVacDate
      };
      setFormData(prev => ({
        ...prev,
        vaccinations: [...(prev.vaccinations || []), newVac]
      }));
      setNewVacName('');
      setNewVacDate('');
    }
  };

  const removeVaccination = (id: string) => {
    setFormData(prev => ({
      ...prev,
      vaccinations: prev.vaccinations?.filter(v => v.id !== id)
    }));
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setIsUploading(true);
      try {
        const compressedBase64 = await compressImage(e.target.files[0]);
        setFormData(prev => ({
          ...prev,
          photos: [...(prev.photos || []), compressedBase64]
        }));
      } catch (err) {
        alert("Error al cargar la imagen");
      } finally {
        setIsUploading(false);
      }
    }
  };

  // Helper to generate deterministic ID
  const generateId = (name: string, date: string) => {
      const cleanName = name.toLowerCase().trim().replace(/[^a-z0-9]/g, '_');
      return `${cleanName}_${date}`;
  };

  const handleSaveClick = () => {
    const finalData = { ...formData };

    // Check if user typed a vaccination but forgot to click '+'
    if (newVacName && newVacDate) {
        const pendingVac: Vaccination = {
            id: crypto.randomUUID(),
            name: newVacName,
            date: newVacDate
        };
        finalData.vaccinations = [...(finalData.vaccinations || []), pendingVac];
    }

    // ID GENERATION LOGIC
    if (!initialData) {
        // Create Mode: Generate ID from Name + Date
        if (finalData.name && finalData.admissionDate) {
            finalData.id = generateId(finalData.name, finalData.admissionDate);
        } else {
            // Fallback (should normally be prevented by UI validation)
            finalData.id = crypto.randomUUID();
        }
    } 
    // Edit Mode: keep existing ID (finalData.id is already set from initialData)

    onSave(finalData as Cat);
  };

  const handleDeleteClick = async () => {
      if (initialData && initialData.id) {
          setIsDeleting(true);
          // Wait for result!
          // onDelete handles confirmation dialog internally in CatManager
          const success = await onDelete(initialData.id);
          setIsDeleting(false);
          
          if (success) {
              onClose();
          }
      }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl animate-[fadeIn_0.3s_ease-out]">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white p-4">
          <h2 className="text-xl font-bold text-gray-800">
            {initialData ? 'Editar gato' : 'Admitir nuevo gato'}
          </h2>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-gray-100">
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Main Info */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">Nombre</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 p-2 focus:border-purple-500 focus:ring-purple-500"
                placeholder="ej. Luna"
                disabled={!!initialData} // Optional: Lock name on edit to prevent confusion about ID? Usually better to allow edit but keep ID.
              />
              {!initialData && formData.name && formData.admissionDate && (
                  <p className="text-xs text-gray-400 mt-1">
                      ID será: {generateId(formData.name, formData.admissionDate)}
                  </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Sexo</label>
              <div className="mt-1 flex space-x-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={formData.gender === 'female'}
                    onChange={() => handleChange('gender', 'female')}
                    className="text-purple-600 focus:ring-purple-500"
                  />
                  <span>Femenino</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={formData.gender === 'male'}
                    onChange={() => handleChange('gender', 'male')}
                    className="text-purple-600 focus:ring-purple-500"
                  />
                  <span>Masculino</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Terrain</label>
              <div className="mt-1 flex space-x-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={formData.terrain === 1}
                    onChange={() => handleChange('terrain', 1)}
                    className="text-purple-600 focus:ring-purple-500"
                  />
                  <span>T1</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={formData.terrain === 2}
                    onChange={() => handleChange('terrain', 2)}
                    className="text-purple-600 focus:ring-purple-500"
                  />
                  <span>T2</span>
                </label>
              </div>
            </div>

            <div>
               <label className="block text-sm font-medium text-gray-700">Padrino</label>
               <select
                 value={formData.godparent || ''}
                 onChange={(e) => handleChange('godparent', e.target.value)}
                 className="mt-1 block w-full rounded-md border border-gray-300 p-2 focus:border-purple-500 focus:ring-purple-500"
               >
                 <option value="">Sin padrino</option>
                 {existingGodparents.map(gp => <option key={gp} value={gp}>{gp}</option>)}
               </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Fecha de ingreso</label>
              <input
                type="date"
                value={formData.admissionDate}
                onChange={(e) => handleChange('admissionDate', e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 p-2 focus:border-purple-500 focus:ring-purple-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Fecha de nacimiento (opcional)</label>
              <input
                type="date"
                value={formData.birthDate || ''}
                onChange={(e) => handleChange('birthDate', e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 p-2 focus:border-purple-500 focus:ring-purple-500"
              />
            </div>
            
             <div>
              <label className="block text-sm font-medium text-gray-700">Fecha de fallecimiento (opcional)</label>
              <input
                type="date"
                value={formData.deathDate || ''}
                onChange={(e) => handleChange('deathDate', e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 p-2 focus:border-purple-500 focus:ring-purple-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Número de pasaporte</label>
              <input
                type="text"
                value={formData.passportNumber || ''}
                onChange={(e) => handleChange('passportNumber', e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 p-2 focus:border-purple-500 focus:ring-purple-500"
                placeholder="ej. ES123456789"
              />
            </div>

            <div className="md:col-span-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <FileText className="h-4 w-4" /> Notas (opcional)
              </label>
              <textarea
                value={formData.notes || ''}
                onChange={(e) => handleChange('notes', e.target.value)}
                rows={3}
                className="mt-1 block w-full rounded-md border border-gray-300 p-2 focus:border-purple-500 focus:ring-purple-500"
                placeholder="Carácter, historia previa, particularidades..."
              />
            </div>
          </div>

          {/* Chip Section */}
          <div className="rounded-lg bg-gray-50 p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Información del chip</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Número de serie</label>
                <input
                  type="text"
                  value={chipSerialNumber}
                  onChange={(e) => setChipSerialNumber(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                  placeholder="ej. 123456789012345"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lugar de implantación</label>
                <input
                  type="text"
                  value={chipLocation}
                  onChange={(e) => setChipLocation(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                  placeholder="ej. cuello izquierdo"
                />
              </div>
            </div>
            {formData.chip && (
              <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded-lg">
                <span className="text-sm text-green-700 font-medium">✓ El gato tiene chip</span>
              </div>
            )}
          </div>

          {/* Toggles - Stack on Mobile */}
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 rounded-lg bg-gray-50 p-4">
            <label className="flex cursor-pointer items-center space-x-3">
              <input
                type="checkbox"
                checked={formData.isNeutered}
                onChange={(e) => handleChange('isNeutered', e.target.checked)}
                className="h-5 w-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <span className="font-medium text-gray-700">Castrado</span>
            </label>
          </div>

          {/* Photos */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Fotos</label>
            <div className="flex flex-wrap gap-4">
              {formData.photos?.map((photo, idx) => (
                <div key={idx} className="relative h-24 w-24 overflow-hidden rounded-lg shadow-sm group">
                  <img src={photo} alt="Cat" className="h-full w-full object-cover" />
                  <button
                    onClick={() => {
                        const newPhotos = [...(formData.photos || [])];
                        newPhotos.splice(idx, 1);
                        handleChange('photos', newPhotos);
                    }}
                    className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100"
                  >
                    <Trash2 className="h-6 w-6 text-white" />
                  </button>
                </div>
              ))}
              <label className={`flex h-24 w-24 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                {isUploading ? <Loader2 className="h-6 w-6 text-purple-500 animate-spin" /> : <Camera className="h-6 w-6 text-gray-400" />}
                <span className="mt-1 text-xs text-gray-500">{isUploading ? '...' : 'Añadir'}</span>
                <input type="file" className="hidden" accept="image/*" onChange={handlePhotoUpload} disabled={isUploading} />
              </label>
            </div>
            <p className="text-xs text-gray-400 mt-2">Las imágenes se guardan comprimidas automáticamente.</p>
          </div>

          {/* Vaccinations */}
          <div className="border-t pt-4">
            <h3 className="mb-3 font-medium text-gray-900">Vacunas</h3>
            <div className="mb-3 space-y-2">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <input
                  type="text"
                  value={newVacName}
                  onChange={(e) => setNewVacName(e.target.value)}
                  placeholder="Nombre de la vacuna"
                  list="vaccination-list"
                  className="rounded-md border border-gray-300 p-2 text-sm"
                />
                <datalist id="vaccination-list">
                    {existingVaccinations.map(v => <option key={v} value={v} />)}
                </datalist>
                <input
                  type="date"
                  value={newVacDate}
                  onChange={(e) => setNewVacDate(e.target.value)}
                  placeholder="Impfdatum"
                  className="rounded-md border border-gray-300 p-2 text-sm"
                />
                <button
                  type="button"
                  onClick={handleAddVaccination}
                  className="rounded-md bg-purple-100 px-3 py-2 text-purple-700 hover:bg-purple-200 flex items-center justify-center"
                >
                  <Plus className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              {formData.vaccinations?.map((vac) => (
                <div key={vac.id} className="flex items-center justify-between rounded-md bg-gray-50 p-2 text-sm">
                  <div className="flex items-center">
                    <span className="font-medium">{vac.name}</span>
                    <span className="mx-2 text-gray-400">•</span>
                    <span className="text-gray-600">{new Date(vac.date).toLocaleDateString('de-DE')}</span>
                  </div>
                  <button onClick={() => removeVaccination(vac.id)} className="text-red-400 hover:text-red-600">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              {(!formData.vaccinations || formData.vaccinations.length === 0) && !newVacName && (
                <p className="text-sm text-gray-400 italic">No hay vacunas registradas</p>
              )}
            </div>
            {/* Next Planned Vaccination */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <h4 className="mb-3 font-medium text-gray-900">Próxima vacuna programada</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de la vacuna</label>
                  <input
                    type="text"
                    value={nextVaccinationName}
                    onChange={(e) => setNextVaccinationName(e.target.value)}
                    placeholder="z.B. FSME"
                    className="w-full rounded-md border border-gray-300 p-2 text-sm focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Geplantes Datum</label>
                  <input
                    type="date"
                    value={nextVaccinationDate}
                    onChange={(e) => setNextVaccinationDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full rounded-md border border-gray-300 p-2 text-sm focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
              </div>
              {formData.nextVaccination && (
                <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <span className="text-sm text-blue-700 font-medium">
                    Geplant: {formData.nextVaccination.name} am {new Date(formData.nextVaccination.date).toLocaleDateString('de-DE')}
                  </span>
                </div>
              )}
            </div>          </div>
        </div>

        <div className="sticky bottom-0 border-t bg-gray-50 p-4 flex justify-between items-center">
          {initialData ? (
             <button
                type="button"
                onClick={handleDeleteClick}
                disabled={isDeleting}
                className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 border border-transparent hover:border-red-200 disabled:opacity-50"
             >
                {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} 
                Löschen
             </button>
          ) : (
             <div></div> // Spacer
          )}

          <div className="flex gap-3">
            <button
                onClick={onClose}
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
                Abbrechen
            </button>
            <button
                onClick={handleSaveClick}
                disabled={!formData.name || isUploading}
                className="rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-2 text-sm font-bold text-white shadow-md hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50"
            >
                Speichern
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};